import { Box, Popover, PopoverArrow, PopoverContent, PopoverTrigger, Portal, Text } from "@chakra-ui/react";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDebounce } from "react-use";
import { SearchBox } from "@familyzone/component-library";

export interface IdentitySearchParams<T = null> {
  query: string;
  pageSize: number;
  extra?: T;
}

export interface IdentitySearchProps<T, E = null> {
  searchFn: (params: IdentitySearchParams<E>) => Promise<T[]>;
  setState: React.Dispatch<React.SetStateAction<T[]>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  pageSize: number;
  refresh?: boolean;
  setRefresh?: React.Dispatch<React.SetStateAction<boolean>>;
  initialSearch?: string;
  extraParams?: E;
  disabled?: boolean;
  minimumSearchLength?: number;
  placeholder?: string;
  onError?: (error: unknown) => void;
}

export const IdentitySearch = <T, E = null>({
  searchFn,
  setState,
  setLoading,
  pageSize,
  extraParams,
  refresh,
  setRefresh,
  onError,
  initialSearch = "",
  disabled = false,
  minimumSearchLength = 3,
  placeholder = "Search",
}: IdentitySearchProps<T, E>): JSX.Element => {
  const POPOVER_Z_INDEX = 9999999;
  const DEBOUNCE_TIME_MS = 500;
  const EMPTY_SEARCH = "";

  const [rawInput, setRawInput] = useState<string>(initialSearch);
  const [debouncedInput, setDebouncedInput] = useState<string>(initialSearch);
  const onErrorRef = useRef(onError);
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const cache = useRef<T[] | undefined>(undefined);

  const fetch = useCallback(async () => {
    try {
      if (cache.current === undefined && debouncedInput.length < minimumSearchLength) {
        // Case 1: no cache and no search term, fetch only the initial state.
        setLoading(true);
        const result = await searchFn({ query: EMPTY_SEARCH, pageSize: pageSize, extra: extraParams });
        cache.current = result;
        setState(result);
      } else if (cache.current === undefined && debouncedInput.length >= minimumSearchLength) {
        // Case 2: no cache and a search term, fetch both initial and search state.
        setLoading(true);
        const [initialResult, searchResult] = await Promise.all([
          searchFn({ query: EMPTY_SEARCH, pageSize: pageSize, extra: extraParams }),
          searchFn({ query: debouncedInput, pageSize: pageSize, extra: extraParams }),
        ]);
        cache.current = initialResult;
        setState(searchResult);
      } else if (cache.current !== undefined && debouncedInput.length >= minimumSearchLength) {
        // Case 3: cache and a search term, fetch only the search state.
        setLoading(true);
        const searchResult = await searchFn({ query: debouncedInput, pageSize: pageSize, extra: extraParams });
        setState(searchResult);
      } else {
        // Case 4: cache and no search term, do nothing!
      }
    } catch (error) {
      if (onErrorRef.current) onErrorRef.current(error);
    } finally {
      setLoading(false);
    }
  }, [cache, debouncedInput, extraParams, minimumSearchLength, pageSize, searchFn, setLoading, setState]);

  useEffect(() => {
    if (refresh && setRefresh !== undefined) {
      setRefresh(false);
      cache.current = undefined;
      void fetch();
    }
  }, [fetch, refresh, setRefresh]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // If the extra parameters change, invalidate the cache.
  useEffect(() => {
    cache.current = undefined;
  }, [extraParams]);

  // Debounce the search input.
  useDebounce(
    () => {
      const input = rawInput?.trim() || "";
      if (input.length >= minimumSearchLength) {
        setDebouncedInput(input);
      }
    },
    DEBOUNCE_TIME_MS,
    [rawInput]
  );

  // Show the popover when the search is too short.
  const [, cancelPopover] = useDebounce(
    () => {
      const input = rawInput?.trim() || "";
      if (input.length >= 1 && input.length < minimumSearchLength) {
        setPopoverOpen(true);
      }
    },
    DEBOUNCE_TIME_MS,
    [rawInput]
  );

  useEffect(() => {
    // If we've got an empty or valid search, hide the popover.
    if (rawInput.length === 0 || rawInput.length >= minimumSearchLength) {
      setPopoverOpen(false);
      cancelPopover();
    }

    // We have a cache and the search was cleared, clear the debounced input and reload state from cache.
    if (cache.current !== undefined && rawInput.length < minimumSearchLength) {
      setDebouncedInput(EMPTY_SEARCH);
      setState(cache.current);
    }
  }, [cancelPopover, cache, minimumSearchLength, rawInput.length, setState]);

  useEffect(() => {
    if (disabled) return;
    void fetch();
  }, [disabled, fetch]);

  return (
    <Popover
      isOpen={popoverOpen}
      placement="bottom"
      closeOnBlur={false}
      closeOnEsc={false}
      autoFocus={false}
      returnFocusOnClose={false}
      isLazy
      modifiers={[
        {
          name: "zIndex",
          enabled: true,
          phase: "write",
          fn: ({ state }: { state: { styles?: { popper?: { zIndex?: number } } } }) => {
            if (state.styles?.popper) {
              state.styles.popper.zIndex = POPOVER_Z_INDEX;
            }
          },
        },
      ]}
    >
      <PopoverTrigger>
        <Box>
          <SearchBox
            aria-label="search"
            data-testid="identity-search"
            isDisabled={disabled}
            placeholder={placeholder}
            value={rawInput}
            onChange={(e) => setRawInput(e.target.value)}
            onClear={() => setRawInput(EMPTY_SEARCH)}
          />
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent boxShadow={20}>
          <PopoverArrow bg="neutrals.700" boxShadow={20} />
          <Box padding="8px 12px" borderRadius="6px" bg="neutrals.700" boxShadow="0px 4px 10px -1px rgb(0, 0, 0)">
            <Text color="white" fontSize="sm" fontWeight="medium">
              Enter at least {Math.trunc(minimumSearchLength)} characters
            </Text>
          </Box>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
